import { FC, Fragment, memo, useContext, useEffect, useState } from "react";
import styles from "./footer-1.module.css";
import { useFooterLogic } from "../../footer.hooks";
import QRCode from "react-qr-code";
import {
  AppLink,
  TemplateType,
  footerUrls,
  APP_DOWNLOAD_QR_URL,
  Country } from
"@commons-monorepo/index";
import { UiButton } from "@ui-monorepo/index";
import StoreVerificationBadge from "../../../store-verification-badge";
import { TFunction } from "i18next";
import PaymentsMethods from "../../components/payment-methods";
import { FooterContext } from "../../footer.context";

type Footer1Props = {
  variant?: string;
  storeVerifiedFrom?: string;
  t: TFunction;
};

const Footer1: FC<Footer1Props> = ({ variant, storeVerifiedFrom, t }) => {
  const { theme, data } = useContext(FooterContext);
  const showSumerBrand = data?.storeMetadata?.prime?.membershipId == null;
  const { terms, dataTreatment, landingGrowth } = footerUrls;
  const {
    termsConditionsUrl,
    termsConditionsExternalUrl,
    storeMetadata,
    catalogueHomeUrl,
    partnerProfilePicture,
    partnerSocials,
    footerTypeClass,
    showSic,
    handleSocialButton,
    handleGoLanding,
    contactToShop,
    footerConfig,
    addressActive,
    scheduleResume,
    imageSicWhite,
    imageSicBlack,
    paymentMethods,
    lightMode
  } = useFooterLogic();

  const [isFromGoogle, setIsFromGoogle] = useState(false);
  useEffect(() => {
    setIsFromGoogle(document.referrer.includes("google"));
  }, []);

  const PartnerSocialsElement = memo(() => {
    return (
      <>
        {partnerSocials.map((social) =>
        <a
          key={`footer-partner-social-${social.iconName}`}
          href={social.url}
          target='_blank'
          aria-label={social.source}
          className={`${styles.iconLink} icon-link`}
          onClick={() => handleSocialButton(social.source)}
          rel='noreferrer'>

            <span className={`${styles[social.iconName]} ${social.iconName}`} />
          </a>
        )}
      </>);

  });

  const TermsElement = memo(() => {
    return (
      <div className={styles.termDisclaimer}>
        <StoreVerificationBadge
          storeMetadata={storeMetadata}
          storeVerifiedFrom={storeVerifiedFrom}
          lightMode={lightMode} />

        {variant === TemplateType.FOOTER_2 && <StoreData />}
        <div className={styles.termsLink}>
          {t("footer.termsLink__one")}{" "}
          {termsConditionsExternalUrl ?
          <a
            href={termsConditionsExternalUrl}
            className='link'
            target='_blank'
            rel='noreferrer'>

              {t("footer.terms_button")}
            </a> :

          <AppLink to={termsConditionsUrl} className='link'>
              {t("footer.terms_button")}
            </AppLink>}
          {" "}
          {t("footer.termsLink__two")}
        </div>
        <hr className={styles.termsSeparator} />
        <p className={styles.terms}>
          {t("footer.terms__one")}{" "}
          <a href={terms} className='link' target='_blank' rel='noreferrer'>
            {t("footer.terms_button")}
          </a>{" "}
          <span
            dangerouslySetInnerHTML={{
              __html: t("footer.terms__two", {
                dataTreatmentLink: dataTreatment,
                interpolation: { escapeValue: false }
              })
            }} />

        </p>
        {showSumerBrand &&
        <div className={styles.disclaimer}>{t("footer.disclaimer")}</div>}

        {variant === TemplateType.FOOTER_1 &&
        <>
            <hr className={styles.termsSeparator} />
            <Sic />
          </>}

        {paymentMethods &&
        paymentMethods.length > 0 &&
        variant === TemplateType.FOOTER_1 &&
        <div className={styles.paymentsMethodsMobile}>
              <hr className={styles.termsSeparator} />
              <PaymentsMethods
            paymentMethods={paymentMethods}
            lightMode={lightMode} />

            </div>}

      </div>);

  });

  const QrApps = memo(() => {
    return (
      <div className={styles.appQrLink}>
        <span className={styles.madeBy}>{t("footer.download_app")}</span>
        <QRCode
          className={styles.img}
          size={80}
          value={APP_DOWNLOAD_QR_URL}
          viewBox={`0 0 80 80`} />

      </div>);

  });

  const getLinkByCountry = (): string => {
    const auxCountries = [
    Country.COLOMBIA,
    Country.MEXICO,
    Country.ARGENTINA,
    Country.PERU];

    const auxCountry = storeMetadata?.country || Country.COLOMBIA;
    switch (true) {
      case Country.ECUADOR === auxCountry:
        return t("footer.keywords.option2");
      case auxCountries.findIndex((country) => country === auxCountry) !== -1:
        return t("footer.keywords.option1");
      default:
        return t("footer.keywords.option3");
    }
  };

  const Sic = memo(() => {
    return (
      <div className={styles.sic}>
        <a href='https://domun.co'>{getLinkByCountry()}</a>
        {showSic &&
        <a
          href='https://sicfacilita.sic.gov.co/SICFacilita/index.xhtml'
          target='_blank'
          rel='noreferrer'>

            {footerConfig?.darkMode ?
          <img src={imageSicWhite} alt='SIC' loading='lazy' /> :

          <img src={imageSicBlack} alt='SIC' loading='lazy' />}

          </a>}

      </div>);

  });

  const StoreData = memo(() =>
  <div className={styles.storeData}>
      <p>{t("footer.contact")}</p>
      <p>
        {storeMetadata.prefixPhone} {storeMetadata.phone}
      </p>
      {scheduleResume && <p>{scheduleResume}</p>}
      <p>{addressActive?.address}</p>
    </div>
  );

  const ButtonsFooter = memo(() =>
  <>
      {theme.templateId === "VITRINA" || theme.templateId === "CLOUD" ?
    <button className={styles.helpButton} onClick={() => contactToShop()}>
          {t("footer.text_button")} <span className={"icon-head-phones"} />
        </button> :

    <UiButton
      className={styles.helpButton2}
      rightIcon={<span className={"icon-head-phones"} />}
      isWhite
      onClick={contactToShop}
      theme={theme}>

          {t("footer.text_button")}
        </UiButton>}

    </>
  );

  return (
    <div
      id='main-footer-1'
      className={`${styles.footer} ${styles[footerTypeClass]} ${
      lightMode && styles.lightMode
      }`}>

      <div className={styles.footerContainer}>
        <footer className={styles.footerContent}>
          <figure className={styles.storeLogo}>
            <AppLink
              to={catalogueHomeUrl}
              className={`${styles.link} ${
              storeMetadata?.profilePicConfiguration?.enableRectangle &&
              styles.rectangle
              }`}>

              <img
                src={partnerProfilePicture}
                alt='Logo principal de la tienda'
                className={styles.logo}
                loading='lazy' />

            </AppLink>
            {variant === TemplateType.FOOTER_2 &&
            partnerSocials?.length > 0 &&
            <div className={styles.partnerSocials}>
                  <PartnerSocialsElement />
                </div>}

            {variant === TemplateType.FOOTER_2 &&
            <div className={styles.buttons}>
                <ButtonsFooter />
              </div>}

          </figure>
          <div className={styles.buttons}>
            {variant === TemplateType.FOOTER_1 && <ButtonsFooter />}
            {variant === TemplateType.FOOTER_1 &&
            <div className={styles.partnerSocials}>
                <PartnerSocialsElement />
              </div>}

            {paymentMethods &&
            paymentMethods.length > 0 &&
            variant === TemplateType.FOOTER_1 &&
            <div className={styles.paymentsMethodsDesktop}>
                  <PaymentsMethods
                paymentMethods={paymentMethods}
                lightMode={lightMode} />

                </div>}

            {variant === TemplateType.FOOTER_2 && <TermsElement />}
          </div>
          {variant === TemplateType.FOOTER_1 && <TermsElement />}
          {variant === TemplateType.FOOTER_2 &&
          <div className={styles.appsLinks}>
              {showSumerBrand &&
            <div className={styles.appsLinksContainer}>
                  <div className={styles.copyrightApp}>
                    <span className={styles.madeBy}>{t("footer.made_by")}</span>
                    <a
                  href={landingGrowth}
                  target={"_blank"}
                  className={styles.brandLogo}
                  onClick={handleGoLanding}
                  rel='noreferrer'
                  aria-label={t("footer.brand_name")}>

                      <img
                    src={footerUrls.domunLogo}
                    alt={t("footer.brand_name")}
                    className={styles.domunLogo} />

                    </a>
                  </div>
                  <hr />
                  <QrApps />
                </div>}

              {paymentMethods && paymentMethods.length > 0 &&
            <div className={styles.paymentsMethodsColumn}>
                  <PaymentsMethods
                paymentMethods={paymentMethods}
                lightMode={lightMode} />

                </div>}

              {showSumerBrand && <Sic />}
            </div>}

          {showSumerBrand && variant === TemplateType.FOOTER_1 && <QrApps />}
        </footer>
      </div>
    </div>);

};

export default Footer1;